import { memo, useMemo } from "react";
import { CSS } from "@stitches/react";

// Effect type for popout video
export type PopoutEffect = "none" | "stars" | "vhs" | "monochrome";

export const POPOUT_EFFECTS: { value: PopoutEffect; label: string }[] = [
  { value: "none", label: "No effects" },
  { value: "stars", label: "Sparkle" },
  { value: "monochrome", label: "Monochrome" },
  { value: "vhs", label: "VHS" },
];

// Effect overlay component - renders visual effects on top of the popout video
export const PopoutEffectOverlay = memo(function PopoutEffectOverlay({
  effect,
}: {
  effect: PopoutEffect;
}) {
  if (effect === "none") return null;

  if (effect === "stars") {
    // Create sparkles with varying sizes and positions
    const sparkles = [...Array(20)].map((_, i) => {
      const size = 2 + Math.random() * 4; // 2-6px dots
      const duration = 4 + Math.random() * 5; // 4-9s cycle
      return {
        size,
        left: Math.random() * 100,
        top: Math.random() * 100,
        // Negative delay starts animation mid-cycle so stars are already visible
        delay: -(Math.random() * duration),
        duration,
        // Random drift values for x and y - larger distances
        driftX1: -40 + Math.random() * 80,
        driftY1: -40 + Math.random() * 80,
        driftX2: -40 + Math.random() * 80,
        driftY2: -40 + Math.random() * 80,
      };
    });

    return (
      <div className="effect-overlay effect-stars">
        <style>{`
          @keyframes sparkleFloat {
            0%, 100% { opacity: 0; transform: translate(0, 0) scale(0.3); }
            20% { opacity: 1; transform: translate(var(--dx1), var(--dy1)) scale(1); }
            50% { opacity: 0.4; transform: translate(var(--dx2), var(--dy2)) scale(0.6); }
            80% { opacity: 1; transform: translate(var(--dx1), var(--dy2)) scale(1); }
          }
        `}</style>
        {sparkles.map((s, i) => (
          <div
            key={i}
            className="sparkle"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animation: `sparkleFloat ${s.duration}s ease-in-out infinite`,
              animationDelay: `${s.delay}s`,
              "--dx1": `${s.driftX1}px`,
              "--dy1": `${s.driftY1}px`,
              "--dx2": `${s.driftX2}px`,
              "--dy2": `${s.driftY2}px`,
            } as React.CSSProperties}
          />
        ))}
      </div>
    );
  }

  if (effect === "vhs") {
    return (
      <div className="effect-overlay effect-vhs">
        <style>{`
          @keyframes vhsScan {
            0% { background-position: 0 0; }
            100% { background-position: 0 8px; }
          }
        `}</style>
        <div className="vhs-noise" />
        <div className="vhs-lines" />
        <div className="vhs-vignette" />
      </div>
    );
  }

  return null;
});

// CSS styles for popout effects - to be spread into StyledPopoutVideo
export const popoutEffectStyles: CSS = {
  // Effect overlay base styles
  "& .effect-overlay": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "21px",
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: 1,
  },

  // Floating stars / sparkles effect - dark overlay behind stars
  "& .effect-stars": {
    backgroundColor: "rgba(17, 17, 17, 0.2)",
  },

  "& .effect-stars .sparkle": {
    position: "absolute",
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 50%, transparent 70%)",
    boxShadow: "0 0 8px 2px rgba(255,255,255,0.6)",
  },

  // VHS effect
  "& .effect-vhs .vhs-noise": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: "url(/images/noise-background.gif)",
    backgroundSize: "150px",
    opacity: 0.07,
    mixBlendMode: "multiply",
  },

  "& .effect-vhs .vhs-lines": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.2) 0px, rgba(0,0,0,0.2) 1px, transparent 1px, transparent 4px)",
    filter: "blur(0.5px)",
  },

  "& .effect-vhs .vhs-vignette": {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "radial-gradient(ellipse at center, transparent 60%, rgba(51, 51, 51, 0.5) 100%)",
  },
};

// Effect variants for StyledPopoutVideo
export const popoutEffectVariants = {
  effect: {
    none: {},
    stars: {
      // Slightly boost contrast for stars against dark overlay
      "& video": {
        filter: "saturate(.9) contrast(1.1)",
      },
    },
    vhs: {
      "& video": {
        // Higher contrast gives a sharper appearance
        filter: "saturate(.83) hue-rotate(3deg) brightness(1.05)",
      },
    },
    monochrome: {
      "& video": {
        filter: "grayscale(1) contrast(1.1)",
      },
    },
  },
};



// Full-screen background sparkle effect for room backgrounds (e.g. Aero)
export const BackgroundEffectOverlay = memo(function BackgroundEffectOverlay({
  effect,
}: {
  effect?: string;
}) {
  // Memoize sparkle data so it's stable across re-renders
  const sparkles = useMemo(() => {
    if (effect !== "sparkles") return [];
    return [...Array(60)].map((_, i) => {
      const size = 2 + Math.random() * 5; // 2-7px dots
      const duration = 6 + Math.random() * 8; // 6-14s cycle (slower than popout)
      return {
        size,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: -(Math.random() * duration),
        duration,
        driftX1: -60 + Math.random() * 120,
        driftY1: -60 + Math.random() * 120,
        driftX2: -60 + Math.random() * 120,
        driftY2: -60 + Math.random() * 120,
      };
    });
  }, [effect]);

  if (effect !== "sparkles") return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      <style>{`
        @keyframes bgSparkleFloat {
          0%, 100% { opacity: 0; transform: translate(0, 0) scale(0.3); }
          20% { opacity: 0.8; transform: translate(var(--dx1), var(--dy1)) scale(1); }
          50% { opacity: 0.3; transform: translate(var(--dx2), var(--dy2)) scale(0.6); }
          80% { opacity: 0.8; transform: translate(var(--dx1), var(--dy2)) scale(1); }
        }
      `}</style>
      {sparkles.map((s, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.6) 50%, transparent 70%)",
            boxShadow: "0 0 10px 3px rgba(255,255,255,0.5)",
            animation: `bgSparkleFloat ${s.duration}s ease-in-out infinite`,
            animationDelay: `${s.delay}s`,
            "--dx1": `${s.driftX1}px`,
            "--dy1": `${s.driftY1}px`,
            "--dx2": `${s.driftX2}px`,
            "--dy2": `${s.driftY2}px`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
});